import React from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from 'modules/views/shared/Modal';
import { ReactComponent as ExclamationRound } from 'assets/images/ExclamationRound.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { deleteWorkerDrugTestingSchedule } from 'modules/state-mgmt/workerData';

import { actionMapForOtherLoaders } from 'constants/actions';
import { useStylesDeleteCertificationModal } from 'modules/views/pages/Certifications/styles';
import { IDrugTestAppointment } from '../types';

interface IDeleteAppointmentModalProps {
  appointment: IDrugTestAppointment | null;
  isVisible: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
}

const DeleteAppointmentModal = ({ appointment, isVisible, onClose, onDeleteSuccess }: IDeleteAppointmentModalProps) => {
  const { t } = useTranslation();
  const classes = useStylesDeleteCertificationModal();
  const dispatch = useAppDispatch();
  const isDeleteLoading = useAppSelector(
    state => state.loader.otherLoaders[actionMapForOtherLoaders.deleteWorkerDrugTestingSchedule]?.isLoading,
  );

  const onDeleteAppointment = () => {
    if (!appointment) return;
    dispatch(deleteWorkerDrugTestingSchedule({ scheduleId: appointment.id }))
      .then(res => {
        if (res.type.includes('fulfilled')) {
          enqueueSnackbar(t('myDrugTesting.deleteAppointmentModal.successMessage'), { variant: 'success' });
          onDeleteSuccess();
          onClose();
        } else {
          enqueueSnackbar(t('myDrugTesting.deleteAppointmentModal.errors.failedToDelete'), { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar(t('myDrugTesting.deleteAppointmentModal.errors.failedToDelete'), { variant: 'error' });
      });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalTitleStyleClass={classes.modalTitle}
      modalContentStyleClass={classes.modalContent}
      modalActionsStyleClass={classes.modalActions}
      testId="delete-appointment-modal"
      Content={
        <>
          <div className={classes.imageParent}>
            <ExclamationRound />
          </div>
          <p className={classes.title}>{t('myDrugTesting.deleteAppointmentModal.title')}</p>
          <p className={classes.warningText}>{t('myDrugTesting.deleteAppointmentModal.message')}</p>
        </>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.button,
          testId: 'delete-appointment-cancel-button',
          onClick: onClose,
        },
        {
          label: t('common.delete'),
          styleClass: classes.button,
          testId: 'delete-appointment-confirm-button',
          isLoading: isDeleteLoading,
          onClick: onDeleteAppointment,
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(DeleteAppointmentModal);
